import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { RelatedChannels } from "@/components/calv/RelatedChannels";
import { CTABlock } from "@/components/calv/CTABlock";
import { Callout } from "@/components/calv/Callout";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/leistungen/performance-marketing/linkedin-ads")({
  head: () => {
    const h = pageHead("pmLinkedinAds", "de");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "LinkedIn Ads Agentur",
          description: "Lead Gen Forms, Conversation Ads, ABM-Strategien — gesteuert auf Pipeline-Beitrag, nicht auf Reach-Vanity.",
          url: "/leistungen/performance-marketing/linkedin-ads",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Start", path: "/" },
          { name: "Leistungen", path: "/leistungen" },
          { name: "Performance Marketing", path: "/leistungen/performance-marketing" },
          { name: "LinkedIn Ads", path: "/leistungen/performance-marketing/linkedin-ads" },
        ])),
      ],
    };
  },
  component: LinkedInAdsDE,
});

const steps = [
  { num: "01", title: "Pipeline-Definition & Analyse (3–5 Tage)", body: "Bevor wir eine einzige LinkedIn-Kampagne anfassen, klären wir die Pipeline-Logik. Was ist ein MQL, was ein SQL, was ein Closed-Won? Welche durchschnittlichen Conversion-Raten zwischen den Stufen? Welcher Customer-LTV? Plus Account-Audit, falls schon LinkedIn-Aktivität läuft, plus Zielgruppen-Audit. Output: konkrete Hypothesen plus realistisches Erwartungs-Management." },
  { num: "02", title: "Setup, Tracking & ABM-Listen (1–2 Wochen)", body: "LinkedIn Insight Tag härten plus Conversions API für Server-side Tracking. CRM-Integration für Pipeline-Attribution. Aufbau der ABM-Listen über Matched Audiences (Account Lists, Contact Lists) — das ist im B2B-Setup oft der größte Hebel. Erste Lead-Gen-Forms-Templates plus Conversation-Ads-Sequenzen vorbereiten. Ziel: produktive erste Kampagnen am Ende der zweiten Woche." },
  { num: "03", title: "Iteration & Optimierung (ab Woche 2–3)", body: "Iteration auf Audience-Schärfe, Creative-Variation, Form-Conversion-Rate. Bei B2B mit langem Sales-Cycle braucht es Geduld bei der Lead-Quality-Bewertung — wir optimieren nicht auf Wochen-CPL, sondern auf Pipeline-Beitrag über mehrere Monate. Aber operative Steuerung passiert wöchentlich." },
  { num: "04", title: "Skalierung & laufender Betrieb (kontinuierlich)", body: "Budget-Skalierung in funktionierenden Audience-Creative-Kombinationen. ABM-Programm-Ausbau wo es Pipeline liefert. Quartalsweise Sales-Marketing-Alignment-Review (welche Leads aus welchen Kampagnen werden tatsächlich Closed-Won). Operative Steuerung in der Regel direkt durch den Geschäftsführer." },
];

const capabilities = [
  { name: "Sponsored Content & Single-Image-Ads", detail: "Hook-Variation · wöchentliche Zyklen" },
  { name: "Lead Gen Forms", detail: "Pre-filled · CRM-Sync" },
  { name: "Conversation Ads", detail: "Demo-Booking · Sales-Erstgespräche" },
  { name: "Document Ads", detail: "Whitepaper · Case Studies" },
  { name: "Account-Based-Marketing", detail: "Matched Audiences · Tiered Accounts" },
  { name: "Insight Tag & Conversions API", detail: "Server-side · CRM-Integration" },
  { name: "Audience-Strategien", detail: "Job-Title · Skill · Industry" },
  { name: "Pipeline-Reporting", detail: "MQL → SQL → Closed-Won" },
];

const audience = [
  { tag: "Primär", title: "B2B Lead-Generation", body: "LinkedIn Ads ist der wichtigste Performance-Kanal für B2B-Setups mit klar definierter Zielgruppe (Unternehmensgröße, Branche, Funktion, Seniorität). Sinnvoll ab Monatsbudgets von etwa €5.000, voll-skalierbar bis sechsstellige Budgets." },
  { tag: "Sekundär", title: "SaaS Enterprise", body: "Funktioniert für Enterprise-SaaS, wo der Sales-Cycle lang ist und einzelne Leads hohen Lifetime Value haben. Account-Based-Marketing über Company-Lists ist hier oft der wirtschaftlichste Hebel." },
  { tag: "Tertiär", title: "Recruiting & Employer Branding", body: "Kann funktionieren, wenn die Zielgruppe scharf definiert ist und das Unternehmen einen klaren Employer-USP hat. Bei generischen Recruiting-Kampagnen ist die Wirtschaftlichkeit selten gegeben." },
  { tag: "Disqualifikation", title: "Wann wir nicht der richtige Partner sind", body: "Bei Monatsbudgets unter €3.000. Wenn die Erwartung “massenhaft günstige Leads” ist — LinkedIn liefert qualitative, nicht quantitative Leads. Wenn die Sales-Pipeline-Logik nicht definiert ist." },
];

const realistic = [
  "Cost-per-MQL-Reduktion von 25–40 % innerhalb von 2–3 Monaten ist in den meisten B2B-Setups realistisch, wenn vorher mit unscharfen Zielgruppen-Definitionen oder schlecht gepflegten Lead-Gen-Forms gearbeitet wurde.",
  "MQL-zu-SQL-Konversionsrate-Steigerung von 30–60 % ist möglich, wenn die Audience-Schärfe systematisch erhöht wird — von “interessante Branchen” zu konkreten Account-Listen plus Contact-Listen.",
  "CPC-Reduktion von 15–25 % durch Audience-Optimierung und Creative-Iteration ist eine typische Bandbreite. Im Gegensatz zu B2C-Plattformen ist CPC bei LinkedIn aber nie das Hauptziel — Pipeline-Beitrag ist es.",
  "ABM-Account-Penetration von 30–50 % der Ziel-Accounts innerhalb von 6 Monaten ist eine typische Größenordnung, wenn ABM systematisch über Matched Audiences plus Sequenced Conversation Ads ausgespielt wird.",
];

const value = [
  ["Pipeline-Steuerung statt Reach-Vanity.", "Wir reporten auf MQL-Cost, SQL-Conversion und Pipeline-Beitrag, nicht auf Click-Through-Rate oder Engagement-Rate."],
  ["ABM-Tiefe statt Spray-and-Pray.", "Account-Based-Marketing über Matched Audiences und Tiered-Account-Logik — präzise, nicht breit."],
  ["CRM-Integration als Pflicht-Bestandteil.", "Conversions API plus CRM-Sync für Pipeline-Attribution über lange B2B-Sales-Cycles."],
  ["Realistisches Erwartungs-Management.", "Wir sagen Ihnen ehrlich, wenn LinkedIn Ads in Ihrem Setup nicht der wirtschaftlichste Erst-Kanal ist."],
  ["Operative Steuerung durch erfahrene Hand.", "In den meisten Mandaten direkt durch den Geschäftsführer."],
  ["Vergütungs-Modell ohne Budget-Kopplung.", "Wir verdienen nicht mehr, wenn Sie mehr Budget einsetzen."],
];

const related = [
  { to: "/leistungen/performance-marketing/google-ads", name: "Google Ads", sub: "Komplementärer Bottom-Funnel-Kanal für B2B" },
  { to: "/leistungen/picazzoo", name: "picazzoo", sub: "B2B-taugliche Visual-Asset-Variation für Sponsored Content" },
  { to: "/leistungen/tracking-datenbasis", name: "Tracking & Datenbasis", sub: "Pipeline-Attribution über lange Sales-Cycles" },
];

const channelLinks = [
  { to: "/leistungen/performance-marketing/microsoft-ads", name: "Microsoft Advertising", sub: "Bing-Suche, Edge und LinkedIn-Audience — Such-Mix-Diversifikation neben Google." },
  { to: "/leistungen/performance-marketing/meta-ads/recruiting", name: "Recruiting-Kampagnen über Meta", sub: "Mitarbeiter-Gewinnung über Meta Ads — eigene Funnel-Logik, eigene Targeting-Disziplin." },
];

function LinkedInAdsDE() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing · LinkedIn Ads"
          headline={<>LinkedIn Ads Agentur — <em>B2B-Performance mit Disziplin,</em> nicht mit Reichweiten-Vanity.</>}
          lead="Lead Gen Forms, Conversation Ads, ABM-Strategien — gesteuert auf Pipeline-Beitrag, nicht auf Vanity-Reach. Calvarius arbeitet als LinkedIn Ads Agentur für B2B-Mandate, SaaS-Enterprise und beratungsintensive Dienstleister — mit sauberer Zielgruppen-Disziplin und Conversion-Tracking, das Sales-Cycle-Realitäten abbildet."
          metaStrip={["B2B-Fokus", "ABM-Methodik", "CRM-Integration"]}
          breadcrumbs={[
            { label: "Start", to: "/" },
            { label: "Leistungen", to: "/leistungen" },
            { label: "Performance Marketing", to: "/leistungen/performance-marketing" },
            { label: "LinkedIn Ads" },
          ]}
        />

        <Section number="02" kicker="Standpunkt" headline={<>Was eine LinkedIn Ads Agentur heute <em>wirklich erfordert.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Wer eine LinkedIn Ads Agentur sucht, hat in der Regel zwei Erfahrungen gemacht: erstens, dass LinkedIn extrem teuer ist (CPCs zwischen 8 und 25 Euro sind normal). Zweitens, dass die Plattform-Empfehlungen oft auf Awareness-Reichweite zielen, nicht auf wirtschaftlich messbare Lead-Generation. Wer LinkedIn Ads über die Vanity-Reichweite steuert, verbrennt Budget. Wer über Click-through-Rate steuert, optimiert auf Schein. Wer über Pipeline-Beitrag steuert, kommt in die Zone, in der LinkedIn Ads für B2B wirtschaftlich tragbar wird.</p>
              <p className="mb-5">Calvarius behandelt LinkedIn Ads als <strong className="font-semibold text-ink">Pipeline-Werkzeug</strong>, nicht als Reichweiten-Werkzeug. Das bedeutet: Wir definieren mit dem Mandanten zuerst die Pipeline-Logik — was kostet ein Marketing Qualified Lead, was ein Sales Qualified Lead, was ein Closed-Won-Customer? Erst dann wird die Kampagnen-Architektur gebaut. Wir nutzen Lead Gen Forms für reibungsarme Erfassung. Wir setzen Conversation Ads ein, wo individualisierte Kontaktanbahnung funktioniert. Wir steuern Account-Based-Marketing-Listen über Matched Audiences und Company List Targeting — präzise, nicht breit.</p>
              <p>Konkret heißt das: CRM-Integration über Conversions API für saubere Pipeline-Attribution. LinkedIn Insight Tag plus Server-side Tracking für Datenqualität. Klare Trennung zwischen Lead-Volume-Kampagnen und ABM-Kampagnen — andere Logik, andere Steuerung, andere Budget-Verteilung. Und ein realistisches Erwartungs-Management: LinkedIn Ads ist im B2B-Setup oft nicht der wirtschaftlichste Erst-Kanal, aber der einzige Kanal, der bestimmte Zielgruppen mit hoher Genauigkeit erreicht. Wann er wirtschaftlich Sinn macht, klären wir vor dem ersten investierten Euro über unsere <Link to="/leistungen/tracking-datenbasis" className="underline">Tracking-&amp;-Datenbasis-Disziplin</Link>.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Zielgruppen-Einordnung" headline={<>Wann Sie eine LinkedIn Ads Agentur <em>brauchen — und wann nicht.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {audience.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Methodik" headline={<>Wie unsere LinkedIn Ads Agentur <em>Mandate startet.</em></>}>
          <ProcessFlow steps={steps} />
          <RevealOnScroll>
            <div className="mt-10 max-w-2xl rounded-lg border border-rule bg-bg-subtle p-7">
              <p className="eyebrow mb-3">Schneller Start als Wettbewerbs-Vorteil</p>
              <p className="text-[15px] leading-relaxed text-ink-2">Während klassische B2B-Agenturen oft sechs bis zehn Wochen brauchen, bevor die erste LinkedIn-Kampagne mit sauberer Pipeline-Anbindung läuft, sind wir innerhalb von zwei bis drei Wochen produktiv. Das ist möglich, weil wir Pipeline-Logik nicht als Workshop-Reihe verkaufen, sondern als Voraussetzung im ersten Gespräch klären.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="Was wir liefern" headline={<>Was unsere LinkedIn Ads Agentur <em>konkret liefert.</em></>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section number="06" kicker="Tooling" headline={<>Tools und Stack unserer <em>LinkedIn-Ads-Steuerung.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Native Plattform</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>LinkedIn Campaign Manager</li>
                  <li>Sales Navigator (Audience-Recherche)</li>
                  <li>Insight Tag &amp; Conversions API</li>
                  <li>Matched Audiences</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Tracking & CRM</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Server-side Conversions API</li>
                  <li>HubSpot, Salesforce, Pipedrive</li>
                  <li>Google Tag Manager (Web + Server)</li>
                  <li>Looker Studio Pipeline-Dashboards</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Calvarius-Tools</p>
                <ul className="space-y-3 text-sm text-ink-2">
                  <li><Link to="/leistungen/picazzoo" className="font-medium text-ink hover:text-cyan-deep">picazzoo →</Link><br/><span className="text-ink-3">Markentreue Visual-Variation für Sponsored Content</span></li>
                  <li><Link to="/leistungen/tracking-datenbasis" className="font-medium text-ink hover:text-cyan-deep">Tracking & Datenbasis →</Link><br/><span className="text-ink-3">Pipeline-Attribution über lange Sales-Cycles</span></li>
                </ul>
              </div>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="Was realistisch ist" headline={<>Realistische Ergebnisse einer <em>LinkedIn-Ads-Optimierung.</em></>}
          caption="Bei B2B mit langen Sales-Cycles ist die wirtschaftliche Validierung über mehrere Monate Pflicht, nicht über Wochen.">
          <RevealOnScroll>
            <ul className="space-y-4">
              {realistic.map((r, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-6">
                  <span className="font-mono text-xs text-cyan-deep">{String(i+1).padStart(2,"0")}</span>
                  <p className="text-[15px] leading-relaxed text-ink-2">{r}</p>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="Was Sie gewinnen" headline={<>Was Sie von unserer <em>LinkedIn Ads Agentur</em> bekommen.</>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {value.map(([h, b]) => (
                <div key={h} className="bg-white p-7">
                  <div className="font-display text-lg text-ink">{h}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <RelatedChannels locale="de" items={channelLinks} />

        <Section variant="tinted" number="09" kicker="Weiter vertiefen" headline={<>Verwandte Disziplinen.</>}
          caption="Diese Themen wirken im Zusammenspiel.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]">
                  <h3 className="font-display text-xl font-semibold text-ink">{r.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{r.sub}</p>
                  <span aria-hidden className="mt-5 font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-lime-deep">→</span>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/leistungen/performance-marketing" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                ← Alle Performance-Marketing-Disziplinen
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="10" kicker="Konkret werden">
          <Callout>
            <strong>Im strategischen Zusammenhang:</strong> LinkedIn Ads ist die Performance-Tactic für bezahlte B2B-Reichweite. Wer LinkedIn als strategischen Kanal betrachtet — Unternehmens-Sichtbarkeit, Geschäftsführer-Präsenz und bezahlte Aktivierung als gemeinsames System — findet die methodische Einordnung unter <Link to="/leistungen/social-media-marketing" className="underline">Social Media Marketing →</Link>.
          </Callout>
          <CTABlock
            headline={<>LinkedIn Ads Audit in 30 Minuten — <em>kostenlos und unverbindlich.</em></>}
            body="In einem ersten Gespräch klären wir, ob LinkedIn Ads in Ihrem B2B-Setup wirtschaftlich Sinn macht — und wenn ja, mit welcher Budget-Größenordnung und Pipeline-Logik. Sie bekommen eine ehrliche Einordnung."
            primary={{ label: "Gespräch vereinbaren", to: "/kontakt" }}
            secondary={{ label: "Performance-Marketing-Übersicht", to: "/leistungen/performance-marketing" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
